import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useArtistStore } from "../../stores/artistStore.js";
import { Button } from "../../ui/Button.js";
import { LpcAvatar } from "./LpcAvatar.js";
import { AvatarFrame } from "./AvatarFrame.js";

function ArtistSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-3 border border-border bg-tape p-5">
      <div className="skeleton h-3 w-12 self-start" />
      <div className="skeleton h-24 w-24" />
      <div className="skeleton h-4 w-20" />
    </div>
  );
}

export function ArtistsList(): JSX.Element {
  const artists = useArtistStore((s) => s.artists);
  const loading = useArtistStore((s) => s.loading);
  const error = useArtistStore((s) => s.error);

  useEffect(() => {
    void useArtistStore.getState().fetch();
  }, []);

  return (
    <div className="flex flex-col gap-10">
      <section className="reveal grid grid-cols-12 gap-6 border-b border-border pb-10">
        <div className="col-span-12 md:col-span-8">
          <p className="catalog-num mb-3">ELENCO / ROSTER</p>
          <h1
            className="font-display text-[56px] leading-[0.9] tracking-tight text-paper md:text-[80px]"
            style={{ fontVariationSettings: '"opsz" 144, "wght" 600, "SOFT" 100, "WONK" 1' }}
          >
            Personagens <span className="italic text-butter">do estúdio</span>
          </h1>
          <p className="mt-4 max-w-prose text-sm leading-relaxed text-ink-muted">
            Cada artista é um pixel-character com identidade visual própria. Você monta via avatar
            builder modular e depois convoca pra ocupar slots em qualquer banda.
          </p>
        </div>
        <div className="col-span-12 flex items-end justify-end md:col-span-4">
          <Link to="/artists/new">
            <Button variant="primary">+ Novo personagem</Button>
          </Link>
        </div>
      </section>

      {error && (
        <p className="border border-vinyl/40 bg-vinyl/5 p-3 font-mono text-xs uppercase tracking-widest2 text-vinyl">
          Erro: {error}
        </p>
      )}

      {loading && artists.length === 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <ArtistSkeleton key={i} />
          ))}
        </div>
      )}

      {!loading && artists.length === 0 && (
        <div className="reveal-1 flex flex-col items-center gap-4 border border-dashed border-border p-12 text-center">
          <p className="font-display text-2xl text-paper md:text-3xl">Sem personagens ainda.</p>
          <p className="font-mono text-xs uppercase tracking-widest2 text-ink-muted">
            Crie o primeiro e comece a montar bandas.
          </p>
          <Link to="/artists/new">
            <Button variant="primary">+ Criar primeiro personagem</Button>
          </Link>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {artists.map((a, i) => (
          <Link
            key={a.id}
            to={`/artists/${a.id}/edit`}
            className={`reveal reveal-${(i % 5) + 1} group`}
          >
            <div className="card-hover flex flex-col items-center gap-3 border border-border bg-tape p-5">
              <span className="catalog-num self-start">#{String(i + 1).padStart(3, "0")}</span>
              <AvatarFrame size={96} showCorners>
                <LpcAvatar layers={a.lpc_layers} size={96} label={a.name} />
              </AvatarFrame>
              <div className="tape-rule" />
              <span
                className="font-display text-lg leading-tight text-paper transition-colors group-hover:text-butter"
                style={{ fontVariationSettings: '"opsz" 32, "wght" 500, "SOFT" 60' }}
              >
                {a.name}
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
